import {
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { API_URLS } from "src/api/api-urls";
import useApiServices from "src/api/use-api-services";
import AppButton from "src/components/common/app-button";
import Iconify from "src/components/iconify";
import Scrollbar from "src/components/scrollbar/Scrollbar";
import { useBoolean } from "src/hooks/use-boolean";
import { PRIMARY, WARNING } from "src/theme/palette";
import { IActivitiesListDto } from "src/types/activities";
import { handleDebouncedSearch } from "src/utils/debounce-search";
import NewReportFormDialog from "./new-report-form-dialog";

const ReportsDialog = ({ onClose, open }: { onClose: VoidFunction; open: boolean }) => {
  // #region States and hooks
  const newReportDialog = useBoolean();
  const { useGetListApi } = useApiServices();
  const [search, setSearch] = useState<string>("");
  // #endregion

  // #region Services
  const { data: activities, isLoading: isLoadingActivities } = useGetListApi<
    IActivitiesListDto,
    { search: string }
  >({
    url: API_URLS.ACTIVITY,
    parameters: {
      search: search,
    },
  });
  // #endregion

  // #region Handlers
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleDebouncedSearch(() => setSearch(e.target.value));
  };
  // #endregion

  return (
    <>
      <Dialog open={open && !newReportDialog.value} onClose={onClose} fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h4">New Report</Typography>
            <IconButton onClick={onClose}>
              <Iconify icon="eva:close-fill" sx={{ cursor: "pointer" }} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <TextField
              variant="outlined"
              placeholder="Search by keyword"
              fullWidth
              onChange={handleSearch}
              InputProps={{
                endAdornment: (
                  <Iconify
                    width={30}
                    height={30}
                    icon={isLoadingActivities ? "line-md:loading-loop" : "eva:search-fill"}
                    sx={{ cursor: "pointer" }}
                  />
                ),
              }}
            />

            <Scrollbar sx={{ height: 100 }}>
              <Stack spacing={1}>
                {activities?.activities.map((act) => (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      sx={{
                        bgcolor: PRIMARY.lighter,
                        width: 40,
                        height: 40,
                      }}
                    >
                      <Iconify icon="tabler:hash" color={PRIMARY.main} width={35} />
                    </Avatar>

                    <Stack sx={{ width: "100%" }}>
                      <Typography variant="subtitle2">{act.keyword}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {act.platform} | {act.start_date} - {act.end_date}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Scrollbar>

            <AppButton
              variant="text"
              startIcon={
                <Avatar
                  sx={{
                    bgcolor: WARNING.lighter,
                    width: 30,
                    height: 30,
                  }}
                >
                  <Iconify icon="tabler:hash" color={WARNING.main} />
                </Avatar>
              }
              label="Add New Keyword"
              fullWidth={false}
              color="warning"
              sx={{ alignSelf: "start" }}
              onClick={newReportDialog.onTrue}
            />
          </Stack>
        </DialogContent>
      </Dialog>

      <NewReportFormDialog
        onCloseReportsDialog={onClose}
        onClose={newReportDialog.onFalse}
        open={newReportDialog.value}
      />
    </>
  );
};

export default ReportsDialog;
